/**
 * Text size scale helpers for anchored ratio-based font-size overrides.
 */

import * as csstree from '../vendor/csstree.esm.js';

export const SIZE_SCALE = [
  { label: 'XS', ratio: 0.7 },
  { label: 'S', ratio: 0.85 },
  { label: 'M', ratio: 1 },
  { label: 'L', ratio: 1.15 },
  { label: 'XL', ratio: 1.3 }
];

export const RATIO_EPSILON = 1e-6;
export const NUDGE_STEP = 0.05;
export const NUDGE_RATIO_MIN = 0.3;
export const NUDGE_RATIO_MAX = 3.0;

export function toPixels(value, rootPx = 16) {
  if (!value) return Number.NaN;
  const v = String(value).trim().toLowerCase();
  if (v.endsWith('px')) return parseFloat(v);
  if (v.endsWith('rem')) return parseFloat(v) * rootPx;
  if (v.endsWith('em')) return parseFloat(v) * rootPx;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : Number.NaN;
}

export function formatFloat(value, decimals = 3) {
  if (!Number.isFinite(value)) return '';
  return String(parseFloat(value.toFixed(decimals)));
}

export function scaleCssLengthExpression(value, ratio) {
  const source = String(value || '').trim();
  const multiplier = Number(ratio);
  if (!source || !Number.isFinite(multiplier) || multiplier <= 0) return '';
  let ast;
  try {
    ast = csstree.parse(source, { context: 'value' });
  } catch {
    return '';
  }
  let changed = false;
  csstree.walk(ast, (node) => {
    if (node.type !== 'Dimension' && node.type !== 'Percentage') return;
    const numeric = Number.parseFloat(node.value);
    if (!Number.isFinite(numeric)) return;
    node.value = formatFloat(numeric * multiplier, 6);
    changed = true;
  });
  return changed ? csstree.generate(ast) : '';
}

export function captureFontContext(data) {
  const family = String(data?.computedFontFamily || '').trim();
  const weight = String(data?.computedFontWeight || '').trim();
  return [family, weight].filter(Boolean).join('|');
}

export function normalizeFontSizeOverride(value) {
  if (!value || typeof value !== 'object') return null;
  if (Number.isFinite(value.anchorPx) && Number.isFinite(value.ratio)) {
    return {
      kind: 'ratio',
      anchor: typeof value.anchor === 'string' ? value.anchor : '',
      anchorPx: Number(value.anchorPx),
      ratio: Number(value.ratio),
      fontContext: typeof value.fontContext === 'string' ? value.fontContext : ''
    };
  }
  const legacyValue = typeof value.legacyValue === 'string' ? value.legacyValue.trim() : '';
  if (legacyValue) {
    return { kind: 'legacy', legacyValue };
  }
  return null;
}

export function captureDefaultFontAnchor(data) {
  const rootPx = toPixels(data?.computedRootFontSize || '16px', 16) || 16;
  const computed = String(data?.computedFontSize || '').trim();
  const anchor = String(data?.authoredFontSize || computed).trim();
  const anchorPx = toPixels(computed, rootPx);
  if (!Number.isFinite(anchorPx) || anchorPx <= 0) return null;
  const result = {
    anchor: anchor || `${formatFloat(anchorPx / rootPx, 4)}rem`,
    anchorPx
  };
  const fontContext = captureFontContext(data);
  if (fontContext) result.fontContext = fontContext;
  return result;
}

function resolveRatioAnchor(normalized, data, rootPx) {
  const currentContext = captureFontContext(data);
  if (!normalized?.fontContext || !currentContext || normalized.fontContext === currentContext) {
    return {
      anchor: normalized.anchor,
      anchorPx: normalized.anchorPx,
      fontContext: normalized.fontContext || currentContext
    };
  }

  // computedFontSize includes the active ratio override. Divide it back out
  // to recover the current font context's unscaled anchor.
  const currentPx = toPixels(data?.computedFontSize || '', rootPx);
  const rebasedPx = currentPx / normalized.ratio;
  if (!Number.isFinite(rebasedPx) || rebasedPx <= 0) {
    return {
      anchor: normalized.anchor,
      anchorPx: normalized.anchorPx,
      fontContext: currentContext
    };
  }
  return {
    anchor: `${formatFloat(rebasedPx / rootPx, 4)}rem`,
    anchorPx: rebasedPx,
    fontContext: currentContext
  };
}

export function deriveFontSizeState(fontSizeOverride, data) {
  const normalized = normalizeFontSizeOverride(fontSizeOverride);
  const rootPx = toPixels(data?.computedRootFontSize || '16px', 16) || 16;
  const anchor = captureDefaultFontAnchor(data);

  if (normalized?.kind === 'legacy') {
    return {
      mode: 'legacy',
      rootPx,
      legacyValue: normalized.legacyValue,
      readout: normalized.legacyValue,
      options: SIZE_SCALE.map((opt) => ({ ...opt, rem: '', px: Number.NaN })),
      activeRatio: null,
      canAdjust: false
    };
  }

  const ratioAnchor =
    normalized?.kind === 'ratio' ? resolveRatioAnchor(normalized, data, rootPx) : null;
  const effectiveAnchorPx = ratioAnchor?.anchorPx ?? anchor?.anchorPx;
  if (!Number.isFinite(effectiveAnchorPx) || effectiveAnchorPx <= 0) {
    return {
      mode: 'none',
      rootPx,
      anchorPx: Number.NaN,
      anchor: '',
      activeRatio: 1,
      currentPx: Number.NaN,
      readout: '',
      options: SIZE_SCALE.map((opt) => ({ ...opt, rem: '', px: Number.NaN })),
      canAdjust: false
    };
  }

  const activeRatio = normalized?.kind === 'ratio' ? Number(normalized.ratio) : 1;
  const currentPx = effectiveAnchorPx * activeRatio;
  const options = SIZE_SCALE.map((opt) => {
    const px = effectiveAnchorPx * opt.ratio;
    return {
      ...opt,
      px,
      rem: `${formatFloat(px / rootPx, 3)}rem`
    };
  });

  return {
    mode: 'ratio',
    rootPx,
    anchorPx: effectiveAnchorPx,
    anchor:
      normalized?.kind === 'ratio'
        ? ratioAnchor?.anchor || anchor?.anchor || ''
        : anchor?.anchor || '',
    fontContext: ratioAnchor?.fontContext || anchor?.fontContext || '',
    activeRatio,
    currentPx,
    readout: `${formatFloat(currentPx / rootPx, 3)}rem`,
    options,
    canAdjust: true
  };
}

export function isRatioMatch(a, b) {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(a - b) < RATIO_EPSILON;
}

export function buildFontSizeOverrideRecord(data, existingOverride, ratio) {
  const normalized = normalizeFontSizeOverride(existingOverride);
  const nextRatio = Number(ratio);
  if (!Number.isFinite(nextRatio)) return null;
  if (isRatioMatch(nextRatio, 1)) return null;

  if (normalized?.kind === 'ratio') {
    const rootPx = toPixels(data?.computedRootFontSize || '16px', 16) || 16;
    const resolved = resolveRatioAnchor(normalized, data, rootPx);
    const result = {
      anchor: resolved.anchor || '',
      anchorPx: resolved.anchorPx,
      ratio: nextRatio
    };
    if (resolved.fontContext) result.fontContext = resolved.fontContext;
    return result;
  }

  const anchor = captureDefaultFontAnchor(data);
  if (!anchor) return null;
  const result = {
    anchor: anchor.anchor,
    anchorPx: anchor.anchorPx,
    ratio: nextRatio
  };
  if (anchor.fontContext) result.fontContext = anchor.fontContext;
  return result;
}
